import type { ConsoleMethods } from './types';

/**
 * Util that generates a hash from a given string
 *
 * @ignore
 * @param {string} text string used to generate the hash
 * @return {*}  {number} The hash number generated from the input string
 */
export const hashCode = (text: string): number => {
  let hash = 0;
  for (let index = 0; index < text.length; index++) {
    // eslint-disable-next-line no-bitwise -- intentional bit manipulation for string hashing
    hash = text.charCodeAt(index) + ((hash << 5) - hash);
  }
  return hash;
};

/**
 * Util that generates an RGB string code from a given hash number
 *
 * @ignore
 * @param {string} textHash number used to generate RGB code
 * @return {*}  {string} The RGB string code
 */
export const intToRGB = (textHash: number): string => {
  // eslint-disable-next-line no-bitwise -- intentional bit masking to derive an RGB value
  const colorString = (textHash & 0x00fffff).toString(16).toUpperCase();
  return colorString.padStart(6, '0');
};

/**
 * Helper method that provides the default style for the logged namespace string.
 *
 * @ignore
 * @memberof SeamlessLogger
 * @param {string} color namespace color
 * @return {*}  {string} styles for the namespace string
 */

const generateNamespaceStyle = (color?: string): string => {
  const bgColor = color ? `background: #${color};` : '';
  return `${bgColor} font-size: 14px; font-weight:bold; color: white; -webkit-text-stroke:.6px black;`;
};

/**
 * Util that checks if the current environment is a browser
 * useful to avoid using window object in SSR
 */
export const isBrowser = () => typeof window !== 'undefined';

/**
 * Transforms a console method to prepend a namespace and apply color styling.
 * @param {string} method - The console method to transform.
 * @param {string} namespace - The namespace to prepend to the log message.
 * @param {string} [color] - The color to apply to the namespace.
 * @returns {Function} The transformed console method.
 */
export const consoleTransformer = (method: Exclude<ConsoleMethods, 'table'>, namespace: string, color?: string) => {
  return (console[method] as (...args: any[]) => void).bind(
    console,
    `%c[${namespace.toUpperCase()}]`,
    generateNamespaceStyle(color),
  );
};
